import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFullFileUrl } from "../../../Helpers/UrlHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faTimes,
  faSave,
  faArrowLeft,
  faEdit,
  faFileAlt,
  faClock,
  faCheckCircle,
  faQuestionCircle,
  faCalendarAlt,
  faGripVertical,
  faRandom,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { toastHelper } from "../../../Helpers/toastHelper";
import ApiServices from "../../Services/ApiServices";
import Loader from "../../Shared/Loader";
import MiniLoader from "../../Shared/MiniLoader";
import RafiqChatBot from "../../Shared/RafiqChatBot";
import * as yup from "yup";
import WarningAlert from "../../Shared/WarningAlert";
import DynamicAlert from "../../Shared/DynamicAlert";
import extractErrorMessage from "../../../Helpers/APIHelper";
import { storageHelper } from "../../../Helpers/storageHelper";
import botImage from "../../../assets/images/bot.png";
import DeleteAlert from "../../Shared/DeleteAlert";
import FileUploader from "../../Shared/FileUploader";
import Scratchpad from "../../Shared/Scratchpad/Scratchpad";
import FloatingToolbox, { FloatingToolboxItem } from "../../Shared/FloatingToolbox/FloatingToolbox";
import { useLanguage } from '../../../contexts/LanguageContext';

const AddEditExamDetails = () => {
  const { t } = useLanguage();
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const [dynamicAlertState, setDynamicAlertState] = useState({
    isOpen: false,
    resolve: null,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    icon: "warning",
    imageUrl: null,
    imageAlt: "",
    showActions: true
  });

  const showDynamicAlert = (options) => {
    return new Promise((resolve) => {
      setDynamicAlertState({
        isOpen: true,
        resolve,
        title: options.title,
        message: options.text,
        confirmText: options.confirmButtonText || t('confirm'),
        cancelText: options.cancelButtonText || t('cancel'),
        icon: options.icon || "warning",
        imageUrl: options.imageUrl,
        imageAlt: options.imageAlt || "Image",
        showActions: options.showConfirmButton !== false
      });
    });
  };

  const handleDynamicAlertConfirm = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: true });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDynamicAlertCancel = () => {
    if (dynamicAlertState.resolve) {
      dynamicAlertState.resolve({ isConfirmed: false });
    }
    setDynamicAlertState((prev) => ({ ...prev, isOpen: false }));
  };
  const [aiUploadLoading, setAiUploadLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isRandomQuestions, setIsRandomQuestions] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);

  const [chatBotStep, setChatBotStep] = useState("initial"); // "initial", "questionCount"
  const [chatBotLoading, setChatBotLoading] = useState(false);
  const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
  const [deleteImageModal, setDeleteImageModal] = useState({
    isOpen: false,
    questionId: null,
    loading: false,
  });

  // Yup schema for a single question
  const questionSchema = yup.object().shape({
    question_text: yup.string().trim().required(t('examDetailsQuestionRequired')),
    options: yup
      .array()
      .of(yup.string().trim().required(t('examDetailsOptionRequired')))
      .length(4, t('examDetailsFourOptions'))
      .test(
        "unique",
        t('examDetailsUniqueOptions'),
        (arr) => Array.isArray(arr) && new Set(arr).size === arr.length
      ),
    correct_answer: yup
      .number()
      .required(t('examDetailsCorrectAnswerRequired'))
      .min(1)
      .max(4),
  });

  // Fetch exam info
  const fetchExam = async () => {
    try {
      const response = await ApiServices.get(`/Exam/${examId}`);
      if (!response.data || Object.keys(response.data).length === 0) {
        setNotFound(true);
        setExam(null);
      } else {
        setExam(response.data);
        setNotFound(false);
      }
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(t('examLoadFailed'));
      }
    }
  };

  // Fetch exam questions
  const fetchExamDetails = async (id) => {
    try {
      const response = await ApiServices.get(`/ExamDetails/${id}`);
      if (response.data) {
        // Handle new API response structure
        const examData = response.data;
        setIsRandomQuestions(examData.is_random_questions || false);

        if (examData.questions && Array.isArray(examData.questions)) {
          const formattedQuestions = examData.questions.map((q) => {
            let opts = parseOptions(q.options);
            // Always pad to 4 options
            if (!Array.isArray(opts)) opts = ["", "", "", ""];
            while (opts.length < 4) opts.push("");
            if (opts.length > 4) opts = opts.slice(0, 4);
            return {
              exam_details_id: q.exam_details_id,
              exam_id: id,
              question_text: q.question,
              question_image: q.question_image || null,
              options: opts,
              correct_answer:
                typeof q.correct_answer === "number" ? q.correct_answer : 1,
              is_new: false,
              ordered: q.ordered || 0,
            };
          });
          // Sort by ordered field if not random
          if (!examData.is_random_questions) {
            formattedQuestions.sort((a, b) => a.ordered - b.ordered);
          }
          setQuestions(formattedQuestions);
        } else {
          // Fallback for old API structure
          const formattedQuestions = response.data.map((q) => {
            let opts = parseOptions(q.options);
            if (!Array.isArray(opts)) opts = ["", "", "", ""];
            while (opts.length < 4) opts.push("");
            if (opts.length > 4) opts = opts.slice(0, 4);
            return {
              exam_details_id: q.exam_details_id,
              exam_id: id,
              question_text: q.question,
              question_image: q.question_image || null,
              options: opts,
              correct_answer:
                typeof q.correct_answer === "number" ? q.correct_answer : 1,
              is_new: false,
              ordered: 0,
            };
          });
          setQuestions(formattedQuestions);
        }
      }
    } catch (error) {
      toastHelper.error(t('examDetailsLoadFailed'));
    }
  };

  // Load both exam and questions in parallel
  const loadExamAndQuestions = async () => {
    setLoading(true);
    await Promise.all([fetchExam(), fetchExamDetails(examId)]);
    setLoading(false);
  };

  useEffect(() => {
    if (examId) {
      loadExamAndQuestions();
    }
    // eslint-disable-next-line
  }, [examId]);

  const parseOptions = (optionsString) => {
    try {
      return JSON.parse(optionsString);
    } catch (error) {
      return ["", "", "", ""];
    }
  };

  const indexToLetter = (index) => {
    return ["A", "B", "C", "D"][index - 1] || "";
  };

  const letterToIndex = (letter) => {
    return ["A", "B", "C", "D"].indexOf(letter) + 1;
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      exam_details_id: Date.now() + Math.random(),
      exam_id: exam.id,
      question_text: "",
      question_image: null,
      options: ["", "", "", ""],
      correct_answer: 1,
      is_new: true,
      ordered: questions.length,
    };
    setQuestions((prev) => [...prev, newQuestion]);
    setEditingQuestion(newQuestion.exam_details_id);
  };

  const handleQuestionChange = (questionId, field, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const questionIndex = updated.findIndex(
        (q) => q.exam_details_id === questionId
      );
      if (questionIndex !== -1) {
        if (field === "correct_answer") {
          updated[questionIndex] = {
            ...updated[questionIndex],
            [field]: letterToIndex(value),
          };
        } else {
          updated[questionIndex] = {
            ...updated[questionIndex],
            [field]: value,
          };
        }
      }
      return updated;
    });
  };

  const handleOptionChange = (questionId, index, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const questionIndex = updated.findIndex(
        (q) => q.exam_details_id === questionId
      );
      if (questionIndex !== -1) {
        const options = [...updated[questionIndex].options];
        options[index] = value;
        // Always keep 4 options
        while (options.length < 4) options.push("");
        if (options.length > 4) options.length = 4;
        updated[questionIndex] = {
          ...updated[questionIndex],
          options,
        };
      }
      return updated;
    });
  };

  const handleDeleteQuestion = async (exam_details_id) => {
    if (questions.length === 1) {
      toastHelper.error(t('examDetailsCannotDeleteLast'));
      return;
    }
    const result = await showDynamicAlert({
      title: t('confirmDeleteTitle'),
      text: t('examDetailsDeleteQuestionConfirm'),
      icon: "warning",
      confirmButtonText: t('examDetailsYesDelete'),
      cancelButtonText: t('cancel'),
    });
    if (!result.isConfirmed) return;
    const question = questions.find(
      (q) => q.exam_details_id === exam_details_id
    );
    if (!question) return;
    if (question.is_new) {
      // Just remove from local state
      const updatedQuestions = questions.filter(
        (q) => q.exam_details_id !== exam_details_id
      );
      setQuestions(updatedQuestions);
      setEditingQuestion(null);
    } else {
      // Call API to delete (send array of integers)
      try {
        setLoading(true);
        await ApiServices.deleteWithData("/ExamDetails/delete", [
          exam_details_id,
        ]);
        toastHelper.success(t('examDeleteSuccess'));
        const updatedQuestions = questions.filter(
          (q) => q.exam_details_id !== exam_details_id
        );
        setQuestions(updatedQuestions);
        setEditingQuestion(null);
      } catch (error) {
        const title = error?.title || error?.message || "";
        if (title.toLowerCase().includes("validation errors")) {
          const errMessage = extractErrorMessage(error);
          toastHelper.error(errMessage);
        } else {
          toastHelper.error(error.msg || t('examDetailsSaveQuestionsFail'));
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const validateQuestions = async () => {
    if (questions.length === 0) {
      toastHelper.error(t('examDetailsAtLeastOneQuestion'));
      return false;
    }
    for (let i = 0; i < questions.length; i++) {
      try {
        await questionSchema.validate(questions[i], { abortEarly: false });
      } catch (err) {
        if (err.inner && err.inner.length > 0) {
          toastHelper.error(`${t('examDetailsQuestionErrorPrefix').replace('{index}', i + 1)}${err.inner[0].message}`);
        } else {
          toastHelper.error(`${t('examDetailsQuestionErrorPrefix').replace('{index}', i + 1)}${err.message}`);
        }
        setEditingQuestion(questions[i].exam_details_id);
        return false;
      }
    }
    return true;
  };

  const convertQuestionsToFormData = (questionsArray, examId, isUpdate = false) => {
    const formData = new FormData();
    questionsArray.forEach((q, index) => {
      if (isUpdate) {
        formData.append(`[${index}].exam_details_id`, q.exam_details_id);
      }
      formData.append(`[${index}].exam_id`, examId);
      formData.append(`[${index}].question`, q.question_text.trim());

      // Append options individually
      q.options.forEach((opt, optIndex) => {
        formData.append(`[${index}].options[${optIndex}]`, opt.trim());
      });

      formData.append(`[${index}].correct_answer`, q.correct_answer);

      if (q.question_image instanceof File) {
        formData.append(`[${index}].question_image`, q.question_image);
      } else if (q.question_image === null) {
        formData.append(`[${index}].question_image`, "");
      }
      // If it's a string (existing image), we don't necessarily need to send it back
      // unless the backend requires it to know it wasn't deleted.
      // But typically for multipart, if the property is missing, it's not updated.
    });
    return formData;
  };

  const handleSave = async () => {
    try {
      const isValid = await validateQuestions();
      if (!isValid) {
        return;
      }
      setLoading(true);
      const newQuestions = questions.filter((q) => q.is_new);
      const existingQuestions = questions.filter((q) => !q.is_new);

      if (newQuestions.length > 0) {
        const formData = convertQuestionsToFormData(newQuestions, exam.id);
        await ApiServices.post("/ExamDetails/bulk/create", formData, true);
      }

      if (existingQuestions.length > 0) {
        const formData = convertQuestionsToFormData(existingQuestions, exam.id, true);
        await ApiServices.put("/ExamDetails/bulk/update", formData, true);
      }
      toastHelper.success(t('examDetailsSaveQuestionsSuccess'));
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error.msg || t('examDetailsSaveQuestionsFail'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) {
      toastHelper.error(t('examDetailsSelectQuestionsToDelete'));
      return;
    }
    const result = await showDynamicAlert({
      title: t('confirmDeleteTitle'),
      text: t('examDetailsDeleteSelectedConfirm'),
      icon: "warning",
      confirmButtonText: t('examDetailsYesDelete'),
      cancelButtonText: t('cancel'),
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await ApiServices.deleteWithData(
        "/ExamDetails/delete",
        selectedQuestions
      );
      toastHelper.success(t('examDetailsDeleteSelectedSuccess'));
      setQuestions(
        questions.filter((q) => !selectedQuestions.includes(q.exam_details_id))
      );
      setSelectedQuestions([]);
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error.msg || t('examDetailsSaveQuestionsFail'));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestionSelection = (exam_details_id) => {
    setSelectedQuestions((prev) =>
      prev.includes(exam_details_id)
        ? prev.filter((id) => id !== exam_details_id)
        : [...prev, exam_details_id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      // Deselect all
      setSelectedQuestions([]);
    } else {
      // Select all
      const allQuestionIds = questions.map((q) => q.exam_details_id);
      setSelectedQuestions(allQuestionIds);
    }
  };

  const isAllSelected = questions.length > 0 && selectedQuestions.length === questions.length;

  const toggleEditMode = (questionId) => {
    setEditingQuestion(editingQuestion === questionId ? null : questionId);
  };

  // Drag and Drop Functions
  const handleDragStart = (e, index) => {
    if (isRandomQuestions) return; // Disable drag if random questions
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (
      isRandomQuestions ||
      draggedIndex === null ||
      draggedIndex === dropIndex
    )
      return;

    const newQuestions = [...questions];
    const draggedQuestion = newQuestions[draggedIndex];

    // Remove dragged question
    newQuestions.splice(draggedIndex, 1);
    // Insert at new position
    newQuestions.splice(dropIndex, 0, draggedQuestion);

    // Update ordered field
    const updatedQuestions = newQuestions.map((q, index) => ({
      ...q,
      ordered: index,
    }));

    setQuestions(updatedQuestions);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Update question order via API
  const updateQuestionOrder = async () => {
    if (isRandomQuestions) return;

    try {
      setOrderLoading(true);
      const questionIds = questions
        .filter((q) => !q.is_new) // Only existing questions
        .map((q) => q.exam_details_id);

      if (questionIds.length === 0) return;

      await ApiServices.put("/ExamDetails/updateOrder", {
        exam_id: parseInt(examId),
        question_ids_ordered: questionIds,
      });

      toastHelper.success(t('examDetailsUpdateOrderSuccess'));
    } catch (error) {
      toastHelper.error(t('examDetailsUpdateOrderFail'));
    } finally {
      setOrderLoading(false);
    }
  };

  const handleAIFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = [".docx", ".pdf", ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".ico", ".heic"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (!allowedTypes.includes(fileExtension)) {
      toastHelper.error(t('examDetailsInvalidFile'));
      return;
    }

    // Check file size (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      toastHelper.error(t('examDetailsFileSizeLimit'));
      return;
    }

    setAiUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await ApiServices.post(
        `/ExamDetails/bulk/AI/create/${exam.id}`,
        formData,
        true // isFormData
      );
      toastHelper.success(t('examDetailsAiUploadSuccess'));
      fetchExamDetails(exam.id);
    } catch (error) {
      toastHelper.error(error.msg || t('examDetailsAiUploadFail'));
    } finally {
      setAiUploadLoading(false);
    }
  };

  // Helper to format date
  const formatExamDate = (dateString) => {
    if (!dateString) return "";
    // Try to format as YYYY-MM-DD
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle option selection in chatbot
  const handleChatBotOptionSelect = async (option, history) => {
    // Initial step: asking if user wants to generate questions
    if (chatBotStep === "initial") {
      if (option.value === "generate_from_mozakara") {
        setChatBotLoading(true);
        try {
          const response = await ApiServices.get(
            `Teacher/Mozakara/GenerateQuestions/${examId}`
          );

          if (Array.isArray(response) && response.length > 0) {

            // Add questions to the exam details
            const formattedQuestions = response.map((q, index) => {
              // Ensure options array has exactly 4 elements
              let opts = Array.isArray(q.options) ? [...q.options] : [];
              while (opts.length < 4) opts.push("");
              if (opts.length > 4) opts = opts.slice(0, 4);

              return {
                exam_details_id: Date.now() + index + Math.random(),
                exam_id: parseInt(examId),
                question_text: q.question || "",
                question_image: null,
                options: opts,
                correct_answer: q.correct_answer !== undefined ? q.correct_answer + 1 : 1, // API uses 0-based, we use 1-based
                is_new: true,
                ordered: questions.length + index,
              };
            });

            setQuestions((prev) => [...prev, ...formattedQuestions]);

            // Save generated questions immediately
            try {
              const formData = convertQuestionsToFormData(formattedQuestions, examId);
              await ApiServices.post("/ExamDetails/bulk/create", formData, true);
              toastHelper.success(t('examDetailsNoteSaveSuccess'));
            } catch (saveError) {

              toastHelper.error(t('examDetailsAutoGenerateSaveFail'));
            }

            return {
              message: t('examDetailsNoteExtractSuccess').replace('{count}', response.length),
              apiResponse: response,
              nextOptions: [],
            };
          } else {
            return {
              message: t('examDetailsNoteNoQuestions'),
            };
          }
        } catch (error) {
          toastHelper.error(t('examDetailsNoteExtractFail'));
          return {
            message: t('examDetailsNoteExtractError'),
          };
        } finally {
          setChatBotLoading(false);
        }
      } else if (option.text === t('yes') || option.value === "yes") {
        // Move to question count step
        setChatBotStep("questionCount");
        return {
          message: t('examDetailsHowManyQuestions'),
          nextOptions: [
            { text: t('examDetailsNQuestions').replace('{n}', 5), value: 5 },
            { text: t('examDetailsNQuestions').replace('{n}', 10), value: 10 },
            { text: t('examDetailsNQuestions').replace('{n}', 15), value: 15 },
            { text: t('examDetailsNQuestions').replace('{n}', 20), value: 20 },
            { text: t('examDetailsNQuestions').replace('{n}', 25), value: 25 },
            { text: t('examDetailsNQuestions').replace('{n}', 30), value: 30 },
            { text: t('examDetailsNQuestions').replace('{n}', 35), value: 35 },
            { text: t('examDetailsNQuestions').replace('{n}', 40), value: 40 },
            { text: t('examDetailsNQuestions').replace('{n}', 45), value: 45 },
            { text: t('examDetailsNQuestions').replace('{n}', 50), value: 50 },
            { text: t('examDetailsNQuestions').replace('{n}', 55), value: 55 },
            { text: t('examDetailsNQuestions').replace('{n}', 60), value: 60 },
          ],
        };
      } else if (option.text === t('no') || option.value === "no") {
        return {
          message: t('examDetailsManualFallback'),
        };
      }
    }

    // Question count step: user selected number of questions
    else if (chatBotStep === "questionCount") {
      const questionCount = option.value || parseInt(option.text) || 10;

      // Validate question count
      if (questionCount < 1 || questionCount > 100) {
        return {
          message: t('examDetailsInvalidQuestionCount'),
          nextOptions: [
            { text: t('examDetailsNQuestions').replace('{n}', 5), value: 5 },
            { text: t('examDetailsNQuestions').replace('{n}', 10), value: 10 },
            { text: t('examDetailsNQuestions').replace('{n}', 15), value: 15 },
            { text: t('examDetailsNQuestions').replace('{n}', 20), value: 20 },
            { text: t('examDetailsNQuestions').replace('{n}', 25), value: 25 },
            { text: t('examDetailsNQuestions').replace('{n}', 30), value: 30 },
            { text: t('examDetailsNQuestions').replace('{n}', 35), value: 35 },
            { text: t('examDetailsNQuestions').replace('{n}', 40), value: 40 },
            { text: t('examDetailsNQuestions').replace('{n}', 45), value: 45 },
            { text: t('examDetailsNQuestions').replace('{n}', 50), value: 50 },
            { text: t('examDetailsNQuestions').replace('{n}', 55), value: 55 },
            { text: t('examDetailsNQuestions').replace('{n}', 60), value: 60 },
          ],
        };
      }

      // Call API to generate questions with count
      try {
        setChatBotLoading(true);
        const response = await ApiServices.post(
          `Exam/GenerateQuestions/${examId}/${questionCount}`,
          {}
        );

        if (Array.isArray(response) && response.length > 0) {

          // Add questions to the exam details
          const formattedQuestions = response.map((q, index) => {
            // Ensure options array has exactly 4 elements
            let opts = Array.isArray(q.options) ? [...q.options] : [];
            while (opts.length < 4) opts.push("");
            if (opts.length > 4) opts = opts.slice(0, 4);

            return {
              exam_details_id: Date.now() + index + Math.random(),
              exam_id: parseInt(examId),
              question_text: q.question || "",
              question_image: null,
              options: opts,
              correct_answer: q.correct_answer !== undefined ? q.correct_answer + 1 : 1, // API uses 0-based (0-3), we use 1-based (1-4)
              is_new: true,
              ordered: questions.length + index,
            };
          });

          setQuestions((prev) => [...prev, ...formattedQuestions]);

          // Save generated questions immediately
          try {
            const formData = convertQuestionsToFormData(formattedQuestions, exam.id);
            await ApiServices.post("/ExamDetails/bulk/create", formData, true);
            toastHelper.success(t('examDetailsGeneratedSaveSuccess'));
          } catch (saveError) {

            toastHelper.error(t('examDetailsAutoGenerateSaveFail'));
          }

          // Reset chatbot state
          setChatBotStep("initial");

          return {
            message: t('examDetailsGeneratedCreateSuccess').replace('{count}', response.length),
            apiResponse: response,
            nextOptions: [], // Clear options after successful generation
          };
        } else {
          return {
            message: t('examDetailsGeneratedCreateFail'),
          };
        }
      } catch (error) {
        toastHelper.error(t('examDetailsGenerateFail'));
        return {
          message: t('examDetailsGenerateError'),
        };
      } finally {
        setChatBotLoading(false);
      }
    }
    return null;
  };

  const handleRemoveImageClick = (questionId, currentImage) => {
    if (currentImage instanceof File || !currentImage) {
      // Client-side or already empty, just clear locally
      handleQuestionChange(questionId, "question_image", null);
    } else {
      // Server-side image, open confirmation modal
      setDeleteImageModal({
        isOpen: true,
        questionId: questionId,
        loading: false,
      });
    }
  };

  const handleConfirmDeleteImage = async () => {
    const { questionId } = deleteImageModal;
    if (!questionId) return;

    try {
      setDeleteImageModal((prev) => ({ ...prev, loading: true }));
      await ApiServices.delete(`/ExamDetails/deleteImage/${questionId}`);

      // Update local state to remove the image
      handleQuestionChange(questionId, "question_image", null);

      toastHelper.success(t('examDetailsDeleteImageSuccess'));
      setDeleteImageModal({ isOpen: false, questionId: null, loading: false });
    } catch (error) {
      toastHelper.error(error.msg || t('examDetailsDeleteImageFail'));
      setDeleteImageModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Get teacher name from storage
  const getTeacherName = () => {
    const fullName = storageHelper.getItem("full_name");
    return fullName || t('examDetailsTeacherFallback');
  };

  if (loading) {
    return <Loader text={t('examDetailsLoadingData')} />;
  }
  if (notFound) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          color: "#dc2626",
          fontSize: "1.5rem",
          fontWeight: 700,
        }}
      >
        <FontAwesomeIcon
          icon={faQuestionCircle}
          style={{ fontSize: "3rem", marginBottom: "1rem", color: "#dc2626" }}
        />
        {t('examDetailsNotFound')}
      </div>
    );
  }
  if (!exam) {
    return null;
  }

  return (
    <motion.div
      className="exam-details-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Rafiq ChatBot Trigger moved to FloatingToolbox */}
      {/* Rafiq ChatBot */}
      <RafiqChatBot
        isOpen={isChatBotOpen}
        onClose={() => {
          setIsChatBotOpen(false);
          // Reset chatbot state when closing
          setChatBotStep("initial");
          setChatBotLoading(false);
        }}
        isAIChat={false}
        welcomeMessage={t('examDetailsWelcomeMessage').replace('{name}', getTeacherName())}
        options={[
          {
            text: t('yes'),
            value: "yes",
          },
          {
            text: t('examDetailsCreateFromNote'),
            value: "generate_from_mozakara",
          },
          {
            text: t('no'),
            value: "no",
          },
        ]}
        onOptionSelect={handleChatBotOptionSelect}
        externalLoading={chatBotLoading}
      />
      <div className="page-header" style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px', minHeight: '48px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('examDetailsPageTitle')}</h1>
        <motion.button
          className="back-btn-modern"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05, x: 5 }}
          whileTap={{ scale: 0.95 }}
          style={{
            position: 'absolute',
            left: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'white',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            color: '#64748b',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </motion.button>
      </div>
      <div className="exam-info-card">
        <div className="exam-title-row">
          <FontAwesomeIcon icon={faFileAlt} className="exam-icon" />
          <span className="exam-title-text">{exam?.exam_title}</span>
        </div>

        <div className="exam-meta">
          <div className="meta-item">
            <FontAwesomeIcon icon={faClock} />
            <span>{t('examDetailsMinutes').replace('{duration}', exam?.exam_duration)}</span>
          </div>
          <div className="meta-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>{formatExamDate(exam?.exam_date)}</span>
          </div>
          <div className="meta-item">
            <FontAwesomeIcon
              icon={isRandomQuestions ? faRandom : faGripVertical}
            />
            <span>{isRandomQuestions ? t('examDetailsRandomOrder') : t('examDetailsManualOrder')}</span>
          </div>
        </div>
        <div className="button-row" style={{ marginTop: '20px' }}>
          <FileUploader
            id="ai-file-upload"
            accept=".docx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.ico,.heic"
            MaxFileSize={20}
            onFileChange={handleAIFileChange}
            placeholder={aiUploadLoading ? t('examDetailsAiUploadLoading') : t('examDetailsAiUploadPlaceholder')}
            subText={t('examDetailsAiUploadSubtext')}
            buttonText={aiUploadLoading ? t('examDetailsAiUploadButtonLoading') : t('examDetailsAiUploadButton')}
            BackgroundColor="#f4f6ff"
            hoverBackgroundColor="#ebf0fa"
            useMiniLoading={aiUploadLoading}
          />
        </div>
      </div>

      <div className="warning-notice">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>{t('examDetailsAiWarning')}</span>
      </div>

      <div className="questions-header">
        <div className="header-left">
          <div className="header-title-with-checkbox">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={toggleSelectAll}
              className="select-all-checkbox"
              title={isAllSelected ? t('examDetailsDeselectAll') : t('examDetailsSelectAll')}
            />
            <h2>{t('examDetailsQuestionsManage').replace('{count}', questions.length)}</h2>
          </div>
          {isRandomQuestions && (
            <div className="random-notice">
              <FontAwesomeIcon icon={faRandom} />
              <span>{t('examDetailsQuestionsRandomNote')}</span>
            </div>
          )}
        </div>
        <div className="header-actions">
          {!isRandomQuestions && (
            <motion.button
              className="btn btn-secondary"
              onClick={updateQuestionOrder}
              disabled={orderLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {orderLoading ? (
                <MiniLoader size={16} color="#2563eb" variant="dots" />
              ) : (
                <FontAwesomeIcon icon={faSave} />
              )}
              <span>{t('examDetailsSaveOrder')}</span>
            </motion.button>
          )}
          <motion.button
            className="btn btn-danger"
            onClick={handleBulkDelete}
            disabled={selectedQuestions.length === 0}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('examDetailsDeleteQuestions')}</span>
          </motion.button>
          <motion.button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading || questions.length === 0}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <>
                <MiniLoader size={16} color="#fff" variant="dots" />
                <span>{t('examDetailsSaving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span>{t('examDetailsSaveAll')}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>

      <motion.div
        className="questions-container enhanced"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        {questions.map((question, index) => (
          <motion.div
            key={question.exam_details_id}
            className={`question-card enhanced ${selectedQuestions.includes(question.exam_details_id)
              ? "selected"
              : ""
              } ${draggedIndex === index ? "dragging" : ""} ${editingQuestion === question.exam_details_id ? "editing" : ""
              }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
            }}
            draggable={!isRandomQuestions}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div className="card-header">
              <div className="question-select">
                <input
                  type="checkbox"
                  checked={selectedQuestions.includes(question.exam_details_id)}
                  onChange={() =>
                    toggleQuestionSelection(question.exam_details_id)
                  }
                />
                <h3>{t('examDetailsQuestionN').replace('{n}', index + 1)}</h3>
                {!isRandomQuestions && (
                  <div className="drag-handle">
                    <FontAwesomeIcon icon={faGripVertical} />
                  </div>
                )}
              </div>
              <div className="card-actions">
                <motion.button
                  className="btn btn-icon"
                  onClick={() => toggleEditMode(question.exam_details_id)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FontAwesomeIcon
                    icon={
                      editingQuestion === question.exam_details_id
                        ? faTimes
                        : faEdit
                    }
                  />
                </motion.button>
                <motion.button
                  className="btn btn-icon btn-danger"
                  onClick={() => handleDeleteQuestion(question.exam_details_id)}
                  disabled={questions.length === 1}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </motion.button>
              </div>
            </div>

            <div className="card-content">
              <AnimatePresence mode="wait">
                {editingQuestion === question.exam_details_id ? (
                  <motion.div
                    key="edit"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="edit-form"
                  >
                    <div className="form-group">
                      <label>{t('examDetailsQuestionTextLabel')}</label>
                      <textarea
                        value={question.question_text}
                        onChange={(e) =>
                          handleQuestionChange(
                            question.exam_details_id,
                            "question_text",
                            e.target.value
                          )
                        }
                        placeholder={t('examDetailsQuestionTextPlaceholder')}
                        className="big-textarea"
                      />
                    </div> {/* Close question text form-group */}

                    <div className="form-group">
                      <label>{t('examDetailsQuestionImage')}</label>
                      <div style={{ position: "relative" }}>
                        <FileUploader
                          previewUrl={
                            question.question_image
                              ? question.question_image instanceof File
                                ? URL.createObjectURL(question.question_image)
                                : getFullFileUrl(question.question_image)
                              : null
                          }
                          onFileChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleQuestionChange(
                                question.exam_details_id,
                                "question_image",
                                file
                              );
                            }
                          }}
                          id={`file-input-${question.exam_details_id}`}
                          MaxFileSize={10}
                          placeholder={t('examDetailsUploadImagePlaceholder')}
                          subText={t('examDetailsUploadImageSubtext')}
                          buttonText={t('examDetailsUploadImageButton')}
                          changeButtonText={t('examDetailsChangeImage')}
                        />
                        {question.question_image && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{ marginTop: "10px", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
                            onClick={() =>
                              handleRemoveImageClick(
                                question.exam_details_id,
                                question.question_image
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            <span style={{ marginRight: "8px" }}>{t('examDetailsDeleteImageBtn')}</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="options-group enhanced">
                      {[0, 1, 2, 3].map((optIndex) => (
                        <motion.div
                          key={optIndex}
                          className="option-input enhanced"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: optIndex * 0.1 }}
                        >
                          <div className="option-radio">
                            <input
                              type="radio"
                              name={`correct_answer_${question.exam_details_id}`}
                              checked={question.correct_answer === optIndex + 1}
                              onChange={() =>
                                handleQuestionChange(
                                  question.exam_details_id,
                                  "correct_answer",
                                  indexToLetter(optIndex + 1)
                                )
                              }
                            />
                            <span className="option-letter">
                              {indexToLetter(optIndex + 1)}
                            </span>
                          </div>
                          <input
                            type="text"
                            value={question.options[optIndex] || ""}
                            onChange={(e) =>
                              handleOptionChange(
                                question.exam_details_id,
                                optIndex,
                                e.target.value
                              )
                            }
                            placeholder={t('examDetailsOptionPlaceholder').replace('{letter}', indexToLetter(optIndex + 1))}
                            className="big-input"
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="view"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="view-content"
                  >
                    {question.question_image && (
                      <div className="question-image-view">
                        <img
                          src={
                            question.question_image instanceof File
                              ? URL.createObjectURL(question.question_image)
                              : getFullFileUrl(question.question_image)
                          }
                          alt="Question"
                          className="view-image"
                          onClick={() => {
                            showDynamicAlert({
                              title: "",
                              text: "",
                              imageUrl: question.question_image instanceof File
                                ? URL.createObjectURL(question.question_image)
                                : getFullFileUrl(question.question_image),
                              imageAlt: "Question Image",
                              showConfirmButton: false,
                              icon: "image" // use 'image' or something other than warning to trigger DynamicAlert
                            });
                          }}
                        />
                      </div>
                    )}
                    <p className="question-text">
                      {question.question_text || t('examDetailsNoQuestionText')}
                    </p>
                    <div className="options-list">
                      {question.options.map((option, optIndex) => (
                        <motion.div
                          key={optIndex}
                          className={`option ${question.correct_answer === optIndex + 1
                            ? "correct"
                            : ""
                            }`}
                          whileHover={{ scale: 1.01 }}
                        >
                          <span className="option-marker">
                            {indexToLetter(optIndex + 1)}
                          </span>
                          <span className="option-text">
                            {option || t('examDetailsOptionPlaceholder').replace('{letter}', indexToLetter(optIndex + 1))}
                          </span>
                          {question.correct_answer === optIndex + 1 && (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="correct-icon"
                            />
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <DeleteAlert
        isOpen={deleteImageModal.isOpen}
        onClose={() => setDeleteImageModal({ isOpen: false, questionId: null, loading: false })}
        onConfirm={handleConfirmDeleteImage}
        loading={deleteImageModal.loading}
        title={t('examDetailsConfirmDeleteImage')}
        message={t('examDetailsConfirmDeleteImageText')}
        confirmText={t('examDetailsDeleteImageConfirmBtn')}
        cancelText={t('examDetailsCancel')}
      />

      <FloatingToolbox>
        <FloatingToolboxItem
          imgSrc={botImage}
          label={t('examDetailsRafeeqAssistant')}
          onClick={() => {
            setIsChatBotOpen(true);
            setChatBotStep("initial");
            setChatBotLoading(false);
          }}
          bg="#000"
        />
        <FloatingToolboxItem
          icon={faPlus}
          label={t('examDetailsAddQuestion')}
          onClick={handleAddQuestion}
          bg="#3b82f6"
        />
        {exam?.use_scratchpad && (
          <FloatingToolboxItem
            icon={faEdit}
            label={t('examDetailsScratchpad')}
            onClick={() => setIsScratchpadOpen(true)}
            bg="#8b5cf6"
          />
        )}
      </FloatingToolbox>

      {exam?.use_scratchpad && (
        <Scratchpad
          externalIsOpen={isScratchpadOpen}
          onCloseExternal={() => setIsScratchpadOpen(false)}
          hideFab={true}
          canDownload={true}
        />
      )}
      <WarningAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon === "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
      />

      <DynamicAlert
        isOpen={dynamicAlertState.isOpen && dynamicAlertState.icon !== "warning"}
        onClose={handleDynamicAlertCancel}
        onConfirm={handleDynamicAlertConfirm}
        title={dynamicAlertState.title}
        message={dynamicAlertState.message}
        confirmText={dynamicAlertState.confirmText}
        cancelText={dynamicAlertState.cancelText}
        confirmColor="#4a90e2"
        imageUrl={dynamicAlertState.imageUrl}
        imageAlt={dynamicAlertState.imageAlt}
        showActions={dynamicAlertState.showActions}
      />
      <style jsx>{`
        .exam-details-container {
          padding: 0;
          background: #ffffff;
          min-height: 100vh;
          direction: rtl;
          position: relative;
          overflow-x: hidden;
          font-family: "Zain", sans-serif;
        }
        .page-header {
          background: #ffffff;
          border-bottom: 1px solid #f1f5f9;
          padding: 2rem 0;
          margin-bottom: 0;
          position: relative;
          overflow: hidden;
        }
        .back-btn {
          background: none;
          border: none;
          color: #6366f1;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .back-btn:hover,
        .back-btn:focus {
          color: #4f46e5;
          outline: none;
          transform: translateX(4px);
        }
        .page-title {
          font-size: 2.2rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0 auto;
          letter-spacing: -0.5px;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .exam-info-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
          padding: 2rem;
          margin: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .exam-info-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
          border-color: #e2e8f0;
        }

        .warning-notice {
          background: #fffbeb;
          border: 1px solid #fef3c7;
          color: #d97706;
          padding: 1rem 1.5rem;
          margin: 0 2rem 2rem 2rem;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 600;
          font-size: 1.1rem;
        }
        .warning-notice svg {
          font-size: 1.25rem;
        }

        .exam-title-row {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }
        .exam-icon {
          color: #6366f1;
          font-size: 2.5rem;
          background: #e0e7ff;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
        }
        .exam-title-text {
          font-size: 1.5rem;
          font-weight: 800;
          color: #0f172a;
        }
        .exam-meta {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }
        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #475569;
          font-size: 1rem;
          font-weight: 600;
          background: #f8fafc;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          border: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }

        .meta-item:hover {
          background: #ffffff;
          border-color: #e2e8f0;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
        }

        .meta-item svg {
          color: #6366f1;
          font-size: 1.2rem;
        }

        /* BUTTONS */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          border: none;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          outline: none;
          font-family: inherit;
        }

        .btn-primary {
          background: #6366f1;
          color: #fff;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        .btn-primary:hover,
        .btn-primary:focus {
          background: #4f46e5;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
        }
        
        .btn-danger {
          background: #ef4444;
          color: #fff;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
        }
        
        .btn-danger:hover,
        .btn-danger:focus {
          background: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(239, 68, 68, 0.25);
        }
        
        .btn-secondary {
          background: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
        }
        
        .btn-secondary:hover,
        .btn-secondary:focus {
          background: #f8fafc;
          color: #0f172a;
          border-color: #cbd5e1;
        }
        
        .btn-icon {
          padding: 0.5rem;
          border-radius: 10px;
          background: #f8fafc;
          color: #64748b;
          border: 1px solid transparent;
          font-size: 1.1rem;
          transition: all 0.2s ease;
        }
        
        .btn-icon:hover,
        .btn-icon:focus {
          background: #f1f5f9;
          color: #6366f1;
        }
        
        .btn:disabled, .btn-icon:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        /* Floating Add Button */
        .floating-add-btn {
          position: fixed;
          bottom: 2.5rem;
          right: 2.5rem;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 64px;
          height: 64px;
          font-size: 1.5rem;
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .floating-add-btn:hover,
        .floating-add-btn:focus {
          transform: translateY(-4px) rotate(90deg);
          box-shadow: 0 12px 28px rgba(99, 102, 241, 0.4);
        }
        /* HEADER */
        .questions-header {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
          padding: 2rem;
          margin: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          transition: all 0.3s ease;
        }
        .header-left {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .header-title-with-checkbox {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .select-all-checkbox {
          width: 22px;
          height: 22px;
          cursor: pointer;
          accent-color: #6366f1;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        .header-left h2 {
          margin: 0;
          font-size: 1.6rem;
          font-weight: 800;
          color: #0f172a;
        }
        .random-notice {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #d97706;
          font-size: 0.95rem;
          font-weight: 600;
          background: #fffbeb;
          padding: 0.5rem 1rem;
          border-radius: 12px;
          border: 1px solid #fef3c7;
        }
        .header-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .questions-container.enhanced {
          margin: 0 2rem 2rem 2rem;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 2rem;
        }
        .question-card.enhanced {
          border: 1px solid #f1f5f9;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .question-card.enhanced:hover {
          border-color: #e2e8f0;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .question-card.enhanced.selected {
          border-color: #6366f1;
          background: #f8fafc;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
        }
        .question-card.enhanced.dragging {
          opacity: 0.6;
          transform: scale(1.02);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        .question-card.enhanced[draggable="true"] {
          cursor: grab;
        }
        .question-card.enhanced[draggable="true"]:active {
          cursor: grabbing;
        }
        .question-card.enhanced.editing {
          border-color: #6366f1;
          background: #ffffff;
          box-shadow: 0 8px 30px rgba(99, 102, 241, 0.15);
        }
        .card-header {
          padding: 1.25rem;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(248, 250, 252, 0.5);
        }
        .card-actions {
          display: flex;
          gap: 0.5rem;
        }
        .question-select {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .question-select h3 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
        }
        .drag-handle {
          color: #cbd5e1;
          padding: 0.25rem;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        .drag-handle:hover {
          color: #64748b;
          background: #f1f5f9;
        }
        .card-content {
          padding: 1.5rem;
        }
        .edit-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .view-content {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        .question-text {
          margin: 0;
          color: #1e293b;
          line-height: 1.6;
          font-size: 1.1rem;
          font-weight: 500;
        }
        .options-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.875rem 1.25rem;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid transparent;
          font-size: 1rem;
          color: #334155;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .option.correct {
          background: #f0fdf4;
          border-color: #bbf7d0;
          color: #166534;
        }
        .option-marker {
          width: 28px;
          height: 28px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          font-weight: 700;
          color: #64748b;
        }
        .option.correct .option-marker {
          background: #22c55e;
          border-color: #22c55e;
          color: white;
        }
        .option-text {
          flex: 1;
        }
        .correct-icon {
          color: #22c55e;
          font-size: 1.25rem;
        }
        /* EDITOR */
        .question-editor.enhanced {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
          padding: 2.5rem;
          margin: 2rem;
          position: relative;
        }

        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2.2rem;
        }
        .stepper {
          display: flex;
          gap: 0.75rem;
          flex-wrap: nowrap;
        }
        .step {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 10px;
          background: #f8fafc;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          border: 1px solid transparent;
          transition: all 0.2s ease;
          user-select: none;
          min-width: 2.5rem;
        }
        .step:not(.active):hover {
          background: #f1f5f9;
          color: #475569;
        }
        .step.active {
          background: #e0e7ff;
          color: #4f46e5;
          border-color: #c7d2fe;
        }
        .question-progress {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }
        .big-textarea {
          min-height: 6rem;
          font-size: 1.1rem;
          padding: 1.25rem;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          transition: all 0.3s ease;
          width: 100%;
          resize: vertical;
          font-family: "Zain", sans-serif;
          color: #0f172a;
        }
        .big-textarea:focus {
          border-color: #6366f1;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
          outline: none;
        }
        .options-group.enhanced {
          margin-top: 1.5rem;
        }
        .option-input.enhanced {
          background: #ffffff;
          border-radius: 16px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
          display: flex;
          align-items: center;
          gap: 1.25rem;
          border: 1px solid #f1f5f9;
          transition: all 0.3s ease;
        }

        .option-input.enhanced:hover {
          border-color: #e2e8f0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
        }

        .big-input {
          font-size: 1.1rem;
          padding: 0.875rem 1.25rem;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          transition: all 0.3s ease;
          flex: 1;
          font-family: "Zain", sans-serif;
          color: #0f172a;
        }
        .big-input:focus {
          border-color: #6366f1;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
          outline: none;
        }
        .helper-text {
          color: #64748b;
          font-size: 0.9rem;
          margin-right: 0.5rem;
          font-weight: 500;
        }
        .editor-footer.enhanced {
          margin-top: 2.5rem;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
        }
        @media (max-width: 900px) {
          .exam-info-card,
          .question-editor.enhanced,
          .questions-grid.enhanced {
            padding: 1.5rem;
            margin: 1rem;
          }
          .floating-add-btn {
            left: 1rem;
            bottom: 1rem;
            padding: 1rem 1.5rem;
            font-size: 1rem;
            border-radius: 16px;
          }
          .page-header {
            padding: 1.5rem 0;
          }
          .page-title {
            font-size: 2rem;
          }
        }
        @media (max-width: 768px) {
          .exam-info-card {
            padding: 1.25rem;
            margin: 1rem 0.5rem;
          }
          .exam-title-row {
            flex-wrap: wrap;
            gap: 0.75rem;
          }
          .exam-icon {
            font-size: 2rem;
          }
          .exam-title-text {
            font-size: 1.25rem;
          }
          .exam-meta {
            flex-direction: column;
            gap: 0.75rem;
          }
          .meta-item {
            font-size: 0.95rem;
            padding: 0.625rem 1rem;
          }
          .button-row {
            margin-top: 0.5rem;
          }
          .gemini-btn {
            width: 100%;
            justify-content: center;
            padding: 0.875rem 1.5rem;
            font-size: 1.1rem;
          }
          .gemini-btn-text {
            font-size: 1.1rem;
            gap: 0.5rem;
          }
          .questions-container.enhanced {
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin: 0 0.5rem 1rem 0.5rem;
          }
          .questions-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
            padding: 1.25rem;
            margin: 1rem 0.5rem;
          }
          .header-title-with-checkbox {
            flex-wrap: wrap;
          }
          .header-left h2 {
            font-size: 1.25rem;
          }
          .header-actions {
            flex-wrap: wrap;
            gap: 0.5rem;
            width: 100%;
          }
          .btn {
            padding: 0.875rem 1.5rem;
            font-size: 0.95rem;
            margin-top: 0;
            flex: 1;
            min-width: 0;
            justify-content: center;
          }
        }
        @media (max-width: 600px) {
          .exam-details-container {
            padding: 0;
            overflow-x: hidden;
          }
          .page-header {
            padding: 1rem 0;
          }
          .page-title {
            font-size: 1.1rem;
            padding: 0 1rem;
          }
          .exam-info-card {
            padding: 1rem;
            margin: 0.75rem 0.5rem;
            border-radius: 12px;
          }
          .exam-title-row {
            gap: 0.5rem;
          }
          .exam-icon {
            font-size: 1.75rem;
          }
          .exam-title-text {
            font-size: 1.1rem;
          }
          .exam-meta {
            gap: 0.5rem;
          }
          .meta-item {
            font-size: 0.875rem;
            padding: 0.5rem 0.875rem;
            gap: 0.5rem;
          }
          .meta-item svg {
            font-size: 1rem;
          }
          .button-row {
            gap: 0.75rem;
            margin-top: 0.75rem;
          }
          .gemini-btn {
            padding: 0.75rem 1.25rem;
            font-size: 1rem;
            gap: 0.5rem;
          }
          .gemini-btn-text {
            font-size: 1rem;
            gap: 0.5rem;
          }
          .gemini-btn-bot-icon {
            width: 18px;
            height: 18px;
          }
          .gemini-logo svg {
            width: 20px;
            height: 20px;
          }
          .file-requirements {
            font-size: 0.75rem;
            margin-top: 0.5rem;
          }
          .questions-header {
            padding: 1rem;
            margin: 0.75rem 0.5rem;
            border-radius: 12px;
          }
          .header-title-with-checkbox {
            gap: 0.5rem;
          }
          .select-all-checkbox {
            width: 18px;
            height: 18px;
          }
          .header-left h2 {
            font-size: 1.1rem;
          }
          .random-notice {
            font-size: 0.8rem;
            padding: 0.5rem 0.75rem;
            gap: 0.375rem;
          }
          .random-notice span {
            white-space: normal;
            overflow: visible;
            text-overflow: unset;
          }
          .header-actions {
            flex-direction: column;
            width: 100%;
          }
          .btn {
            padding: 0.75rem 1.25rem;
            font-size: 0.9rem;
            width: 100%;
            justify-content: center;
          }
          .questions-container.enhanced {
            margin: 0 0.5rem 1rem 0.5rem;
            gap: 1rem;
          }
          .question-card.enhanced {
            border-radius: 12px;
          }
          .card-header {
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
            padding: 0.875rem 1rem 0.5rem 1rem;
          }
          .question-select {
            justify-content: space-between;
            width: 100%;
          }
          .question-select h3 {
            font-size: 1rem;
          }
          .card-actions {
            justify-content: center;
            width: 100%;
          }
          .card-content {
            padding: 1rem;
          }
          .option-input.enhanced {
            flex-direction: column;
            align-items: stretch;
            gap: 0.5rem;
            padding: 0.75rem 0.75rem;
          }
          .option-radio {
            margin-bottom: 0.25rem;
          }
          .big-input {
            width: 100%;
            min-width: 0;
            font-size: 0.95rem;
            padding: 0.75rem 1rem;
          }
          .big-textarea {
            font-size: 1rem;
            padding: 1rem;
            min-height: 4rem;
          }
          .floating-add-btn {
            right: 4.5rem;
            bottom: 0.75rem;
            width: 50px;
            height: 50px;
            font-size: 1.2rem;
            z-index: 10000;
          }
          .rafiq-chatbot-trigger {
            right: 0.75rem;
            left: auto;
            bottom: 0.75rem;
            width: 60px;
            height: 60px;
          }
        }
        /* Question Image Styles */
        .question-image-upload-zone {
          margin-top: 0.5rem;
          border: 2px dashed #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          background: #f8fafc;
          transition: all 0.3s ease;
        }
        .question-image-upload-zone:hover {
          border-color: #3b82f6;
          background: #f1f5f9;
        }
        .image-preview-container {
          position: relative;
          width: 100%;
          padding-top: 56.25%; /* 16:9 Aspect Ratio */
          background: #000;
        }
        .question-image-preview {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .remove-image-overlay {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: rgba(239, 68, 68, 0.9);
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 10;
        }
        .remove-image-overlay:hover {
          background: #dc2626;
          transform: scale(1.1);
        }
        .image-upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 2rem;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s ease;
        }
        .image-upload-placeholder i, .image-upload-placeholder svg {
          font-size: 2rem;
          color: #3b82f6;
        }
        .image-upload-placeholder span {
          font-weight: 600;
          font-size: 0.95rem;
        }
        .question-image-view {
          width: 100%;
          margin-bottom: 1rem;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
          cursor: pointer;
          transition: transform 0.3s ease;
        }
        .question-image-view:hover {
          transform: scale(1.02);
        }
        .view-image {
          width: 100%;
          max-height: 250px;
          object-fit: cover;
          display: block;
        }
        .gemini-btn {
          display: flex;
          align-items: center;
          gap: 1.1rem;
          background: #000;
          border: none;
          border-radius: 2.5rem;
          padding: 0.85rem 2.2rem;
          font-size: 1.25rem;
          font-weight: 700;
          font-family: "Cairo", "Inter", "Arial", sans-serif;
          cursor: pointer;
          transition: box-shadow 0.2s, filter 0.2s, background 0.2s;
          box-shadow: 0 2px 16px 0 rgba(37, 99, 235, 0.1);
          position: relative;
          overflow: hidden;
        }
        .gemini-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .gemini-btn:not(.loading):hover {
          box-shadow: 0 0 24px 0 #7b6cff55, 0 2px 16px 0 rgba(37, 99, 235, 0.13);
          filter: brightness(1.08);
        }
        .gemini-logo {
          display: flex;
          align-items: center;
          margin-left: 0.1rem;
          transition: transform 0.4s cubic-bezier(0.4, 2, 0.6, 1);
        }
        .gemini-btn:not(.loading):hover .gemini-logo {
          transform: rotate(20deg) scale(1.08);
          filter: drop-shadow(0 0 12px #7b6cff88);
        }
        .gemini-logo.spinning {
          animation: gemini-spin 1.2s linear infinite;
        }
        @keyframes gemini-spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .gemini-btn-text {
          background: linear-gradient(
            90deg,
            #00c3ff 0%,
            #7b6cff 50%,
            #ff5ca7 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
          font-weight: 800;
          font-size: 1.25rem;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }
        .gemini-btn .fa-spinner {
          color: #7b6cff;
          margin-left: 0.5rem;
        }
        .gemini-btn-bot-icon {
          width: 24px;
          height: 24px;
          object-fit: contain;
          object-position: center;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }
        @media (max-width: 600px) {
          .gemini-btn {
            font-size: 1rem;
            padding: 0.7rem 1.1rem;
            gap: 0.6rem;
          }
          .gemini-btn-text {
            font-size: 1rem;
          }
          .gemini-btn-bot-icon {
            width: 20px;
            height: 20px;
          }
          .gemini-logo svg {
            width: 22px;
            height: 22px;
          }
        }
        .button-row {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
          margin: 1.2rem 0 0.5rem 0;
        }
        .button-col {
          width: 100%;
          min-width: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .file-requirements {
          text-align: center;
          margin-top: 0.5rem;
          color: #64748b;
          font-size: 0.85rem;
        }

        .file-requirements small {
          color: #94a3b8;
          font-weight: 500;
        }
        .delete-all-btn {
          margin-bottom: 0.5rem;
        }

        /* ChatBot Trigger Button */
        .rafiq-chatbot-trigger {
          position: fixed;
          bottom: 2rem;
          left: 2rem;
          width: 80px;
          height: 80px;
          background: transparent;
          color: white;
          border: none;
          border-radius: 50%;
          font-size: 1.5rem;
          cursor: pointer;
          box-shadow: none;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          animation: buttonPulse 2s ease-in-out infinite;
        }

        @keyframes buttonPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .rafiq-chatbot-trigger:hover {
          box-shadow: none;
          animation: none;
        }

        .chatbot-trigger-icon {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          border-radius: 150px;
          filter: drop-shadow(0 2px 4px rgba(255, 255, 255, 0.3));
        }

        @media (max-width: 768px) {
          .floating-add-btn {
            right: 5rem;
            bottom: 1rem;
            width: 60px;
            height: 60px;
            z-index: 10000;
          }
          .rafiq-chatbot-trigger {
            bottom: 1rem;
            right: 1rem;
            left: auto;
            width: 70px;
            height: 70px;
            font-size: 1.2rem;
            z-index: 9999;
          }
        }

        @media (max-width: 400px) {
          .exam-info-card {
            margin: 0.5rem;
            padding: 0.875rem;
          }
          .exam-title-text {
            font-size: 1rem;
          }
          .exam-icon {
            font-size: 1.5rem;
          }
          .meta-item {
            font-size: 0.8rem;
            padding: 0.5rem 0.75rem;
          }
          .gemini-btn {
            padding: 0.625rem 1rem;
            font-size: 0.95rem;
          }
          .gemini-btn-text {
            font-size: 0.95rem;
          }
          .questions-header {
            margin: 0.5rem;
            padding: 0.875rem;
          }
          .header-left h2 {
            font-size: 1rem;
          }
          .btn {
            padding: 0.625rem 1rem;
            font-size: 0.85rem;
          }
          .questions-container.enhanced {
            margin: 0 0.5rem 0.75rem 0.5rem;
            gap: 0.75rem;
          }
          .question-text {
            font-size: 1rem;
          }
          .option {
            font-size: 0.9rem;
            padding: 0.625rem 0.875rem;
          }
          .floating-add-btn {
            right: 4.5rem;
            bottom: 0.75rem;
            width: 50px;
            height: 50px;
          }
          .rafiq-chatbot-trigger {
            width: 60px;
            height: 60px;
            bottom: 0.75rem;
            right: 0.75rem;
            left: auto;
          }
        }

        /* General mobile improvements */
        @media (max-width: 768px) {
          * {
            -webkit-tap-highlight-color: transparent;
          }
          .btn,
          .option-button,
          .header-action-btn {
            min-height: 44px;
            touch-action: manipulation;
          }
          input[type="text"],
          textarea,
          .big-input,
          .big-textarea {
            font-size: 16px !important;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default AddEditExamDetails;
